import React from 'react';
import { clsx } from 'clsx';
import { SortDownIcon, SortUpIcon } from 'components/icons';
import { columns } from '../../config';
import { SprSimListSortKey, SprSimListTabName } from 'types/your-sims';
import { SortOrder } from 'types/shared';
import { useFilteredSims } from 'lib/hooks/sim/sim.queries';
import SimRow from '../SimListRow/SimListRow';
import { useYourSimsContext } from 'views/YourSiMs/hooks';
import { useGetAllPlans } from 'lib/hooks/plan/plan.queries';
import { Pagination, Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { useEffect } from 'react';

function SimListTable() {
  // calling it here as it takes a lot of time to load the plans
  // so we start loading as soon as user opens the table
  useGetAllPlans();

  const {
    activeTab,
    isUserAllowedToAddClient,
    setOldPageNumber,
    oldPageNumber,
    simsPerPage,
    getFilteredSimsParams,
    oldSelectedSims,
    setOldSelectedSims,
  } = useYourSimsContext();
  const { data, isLoading, isFetching } = useFilteredSims(
    getFilteredSimsParams()
  );

  useEffect(() => {
    if (!data?.records) return;

    const newSelectedSims = data.records.filter((sim) =>
      oldSelectedSims.some((markedSim) => markedSim.id === sim.id)
    );

    setOldSelectedSims(newSelectedSims);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.records]);

  const simRecords = data?.records ?? [];

  if (isLoading) return <LoadingState />;
  if (!isLoading && simRecords.length === 0) return <EmptyState />;

  return (
    <>
      {isFetching && (
        <div
          style={{
            backgroundColor: '#fff',
            textAlign: 'center',
            padding: '10px',
            paddingBottom: '20px',
            borderTopLeftRadius: '6px',
            borderTopRightRadius: '6px',
            marginBottom: '-10px',
          }}
        >
          Refreshing...
        </div>
      )}

      <div className="table-responsive custom-table-shadow">
        <table className="table custom-table custom-table-fixed-col">
          <thead>
            <tr>
              {columns.map((col) => {
                const shouldRenderHeader =
                  col.allowedTabs.includes(activeTab) ||
                  col.allowedTabs === '*';

                // do not render client column if user is not allowed to add client
                if (col.key === 'client' && !isUserAllowedToAddClient)
                  return null;

                return shouldRenderHeader ? (
                  <HeaderCell
                    key={col.key}
                    sortKey={col.sortKey}
                    content={col.headerContent}
                  />
                ) : null;
              })}
            </tr>
          </thead>

          <tbody>
            {simRecords.map((simRecord, i) => (
              <SimRow
                key={`${simRecord.id}-${activeTab}-${i}`}
                simRecord={simRecord}
              />
            ))}
          </tbody>
        </table>
      </div>

      <Pagination
        className="mb-5"
        hideOnSinglePage
        onChange={setOldPageNumber}
        current={oldPageNumber}
        pageSize={simsPerPage}
        defaultCurrent={oldPageNumber}
        total={data?.total}
        showSizeChanger={false}
      />
    </>
  );
}

function LoadingState() {
  return (
    <div className="blank__table__card mt-3">
      <div className="blank__table__card__inner">
        <Spin
          size="large"
          indicator={
            // @ts-ignore
            <LoadingOutlined style={{ fontSize: 30, color: '#000' }} spin />
          }
        />
      </div>
    </div>
  );
}

function EmptyState() {
  const { activeTab } = useYourSimsContext();

  const simListTabDisplayNameMap: ReadonlyMap<SprSimListTabName, string> =
    new Map([
      [SprSimListTabName.ACTIVE, 'Active'],
      [SprSimListTabName.ACTIVATION_IN_PROGRESS, 'Activation In-Progress'],
      [SprSimListTabName.AVAILABLE_PSIM, 'Available'],
      [SprSimListTabName.AVAILABLE_ESIM, 'Available eSim'],
      [SprSimListTabName.PAUSED, 'Paused'],
      [SprSimListTabName.DEACTIVE, 'Deactivated'],
    ]);

  return (
    <div className="blank__table__card mt-3">
      <div className="blank__table__card__inner">
        No {simListTabDisplayNameMap.get(activeTab)} SIMs
      </div>
    </div>
  );
}

type HeaderCellProps = {
  content: React.ReactNode;
  sortKey: SprSimListSortKey | null;
};

function HeaderCell({ content, sortKey }: HeaderCellProps) {
  const { getFilteredSimsParams, setSortingKey } = useYourSimsContext();

  const { sortOrder: listSortOrder, sortBy: listSortBy } =
    getFilteredSimsParams();

  const renderSortIcon = () => {
    if (listSortBy !== sortKey) return <SortUpIcon />;
    return listSortOrder === SortOrder.DESC ? <SortDownIcon /> : <SortUpIcon />;
  };

  const isSortable = sortKey !== null;

  return (
    <th>
      <span
        className={clsx('table-header-title', {
          'cursor-pointer': isSortable,
        })}
        onClick={() => sortKey && setSortingKey(sortKey)}
      >
        {content} {isSortable && renderSortIcon()}
      </span>
    </th>
  );
}

export default SimListTable;
